import {
  Card as AntdCard,
  Badge,
  Button,
  Col,
  Collapse,
  Descriptions,
  Row,
} from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { DeleteOutlined, EditOutlined } from "@ant-design/icons";
import moment from "moment";
import React from "react";
import { Link } from "react-router-dom";
import {
  Company,
  CustomerCompanyAssociation,
} from "../helpers/shared-interfaces";
import { capitalizeFirstLetter } from "../helpers/utilities";
import CardSection from "./CardSection";
import { tabPaneStyle } from "../helpers/constants";
import EntitledSection from "./EntitledSection";
import entitlements from "../config/entitlements";

interface Props {
  style?: React.CSSProperties;
  companyAssociations: CustomerCompanyAssociation[];
  employers: Company[];
  formerCompanies: Company[];
  onAddAssociation?: () => void;
  onEditAssociation?: (association: CustomerCompanyAssociation) => void;
  onDeleteAssociation?: (association: CustomerCompanyAssociation) => void;
  onMarkAssociationAsFormer?: (association: CustomerCompanyAssociation) => void;
  onMarkAssociationAsCurrent?: (
    association: CustomerCompanyAssociation,
  ) => void;
}

const { Panel } = Collapse;

export const CurrentEmployersCard = (props: {
  employer: CustomerCompanyAssociation;
}): JSX.Element => {
  return (
    <div>
      {props.employer.company.name && (
        <CardSection
          headingText="Name"
          data={
            <Link to={`/companies/${props.employer.company.id}`}>
              {props.employer.company.name}
            </Link>
          }
        />
      )}
      <CardSection
        headingText="PetraId"
        data={props.employer.company.petraId ?? "N/A"}
      />
      <CardSection
        headingText="StaffId"
        data={props.employer.staffId ?? "N/A"}
      />
      {props.employer.relationship && (
        <CardSection
          headingText="Company Relationship"
          data={props.employer.relationship}
        />
      )}
      {props.employer.influenceLevel && (
        <CardSection
          headingText="Level Of Influence"
          data={capitalizeFirstLetter(props.employer.influenceLevel)}
        />
      )}
      <CardSection
        headingText="Is Employee"
        data={props.employer.isEmployee ? "Yes" : "No"}
      />
      {props.employer.dateOfEmployment && (
        <CardSection
          headingText="Date Of Employment"
          data={moment(
            props.employer.dateOfEmployment,
            "YYYY-MM-DDTHH:MM:SSZ",
          ).format("L")}
        />
      )}
    </div>
  );
};

export const EmployersCard = (props: {
  employer: Company & { staffId?: string; };
}): JSX.Element => {
  return (
    <div>
      <CardSection
        headingText="Name"
        data={
          <Link to={`/companies/${props.employer.id}`}>
            {props.employer.name}
          </Link>
        }
      />
      <CardSection headingText="PetraId" data={props.employer.petraId ?? "N/A"} />
      <CardSection
        headingText="StaffId"
        data={props.employer?.staffId ?? "N/A"}
      />
    </div>
  );
};

export const CompanyAssociationsCard = (props: {
  companyAssociation: CustomerCompanyAssociation;
}): JSX.Element => {
  return (
    <div>
      <CardSection
        headingText="Name"
        data={
          <Link to={`/companies/${props.companyAssociation.company.id}`}>
            {props.companyAssociation.company.name}
          </Link>
        }
      />
      <CardSection
        headingText="PetraId"
        data={props.companyAssociation.company.petraId ?? "N/A"}
      />
      <CardSection
        headingText="StaffId"
        data={props.companyAssociation.staffId ?? "N/A"}
      />
    </div>
  );
};

export default class CustomerAssociationsView extends React.Component<
  Props,
  Record<string, unknown>
> {
  constructor(props: Props) {
    super(props);
    this.state = {};
  }

  render(): JSX.Element {
    const findAssociation = (companyId: string): CustomerCompanyAssociation | undefined =>
      this.props.companyAssociations.find(
        (association) => association.company?.id === companyId,
      );

    // Deleted associations are inactive and belong in neither section.
    const isActiveAssociation = (company: Company): boolean =>
      findAssociation(company.id)?.isActive !== false;

    const employers = this.props.employers.filter(isActiveAssociation);
    const formerCompanies =
      this.props.formerCompanies.filter(isActiveAssociation);

    return (
      <div
        style={{
          border: "1px solid rgb(232 232 232)",
          padding: "20px",
          boxSizing: "border-box",
          ...this.props.style,
        }}
      >
        <Descriptions
          title={
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <span>Customer Associations</span>
              <EntitledSection entitlement={entitlements.Customers.edit}>
                {this.props.onAddAssociation && (
                  <Button
                    className="base-button"
                    onClick={this.props.onAddAssociation}
                    icon={<PlusOutlined />}
                    style={{ marginLeft: "auto", marginBottom: 15 }}
                  >
                    Add Association
                  </Button>
                )}
              </EntitledSection>
             
            </div>
            
          }
        ></Descriptions>
        <Collapse>
          {employers.length > 0 && (
            <Panel
              header={
                <span>
                  Current Employers{" "}
                  <Badge count={employers.length} style={tabPaneStyle} />
                </span>
              }
              key="employer"
            >
              <Row gutter={[16, 16]}>
                {employers.map((employer: Company): JSX.Element => {
                  const association = findAssociation(employer.id);
                  return (
                    <Col key={employer.id} span={6}>
                      <AntdCard
                        className="associated-entity-card"
                        key={employer.id}
                      >
                        {EmployersCard({
                          employer: {
                            ...employer,
                            staffId: this.props.companyAssociations.find(
                              (item) => item?.company?.id === employer?.id,
                            )?.staffId,
                          },
                        })}
                        <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                          <EntitledSection
                            entitlement={entitlements.Customers.edit}
                          >
                            {association && this.props.onEditAssociation && (
                              <Button
                                onClick={() =>
                                  this.props.onEditAssociation?.(association)
                                }
                                icon={<EditOutlined />}
                              >
                              </Button>
                            )}
                          </EntitledSection>
                          <EntitledSection
                            entitlement={
                              entitlements.Customers.companyAssociation.delete
                            }
                          >
                            {association && this.props.onDeleteAssociation && (
                              <Button
                                onClick={() =>
                                  this.props.onDeleteAssociation?.(association)
                                }
                                icon={<DeleteOutlined />}
                                danger
                              >
                              </Button>
                            )}
                          </EntitledSection>
                          <EntitledSection
                            entitlement={entitlements.Customers.edit}
                          >
                            {association &&
                              this.props.onMarkAssociationAsFormer && (
                                <Button
                                  onClick={() =>
                                    this.props.onMarkAssociationAsFormer?.(
                                      association,
                                    )
                                  }
                                >
                                  Mark as Former
                                </Button>
                              )}
                          </EntitledSection>
                        </div>
                      </AntdCard>
                    </Col>
                  );
                })}
              </Row>
            </Panel>
          )}

          {formerCompanies.length > 0 && (
            <Panel
              header={
                <span>
                  Former Companies{" "}
                  <Badge count={formerCompanies.length} style={tabPaneStyle} />
                </span>
              }
              key="formerCompanies"
            >
              <Row gutter={[16, 16]}>
                {formerCompanies.map((formerCompany: Company): JSX.Element => {
                  const association = findAssociation(formerCompany.id);
                  return (
                    <Col key={formerCompany.id} span={6}>
                      <AntdCard
                        className="associated-entity-card"
                        key={formerCompany.id}
                      >
                        {EmployersCard({
                          employer: formerCompany,
                        })}
                        <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                          <EntitledSection
                            entitlement={entitlements.Customers.edit}
                          >
                            {association &&
                              this.props.onMarkAssociationAsCurrent && (
                                <Button
                                  onClick={() =>
                                    this.props.onMarkAssociationAsCurrent?.(
                                      association,
                                    )
                                  }
                                >
                                  Mark as Current
                                </Button>
                              )}
                          </EntitledSection>
                        </div>
                      </AntdCard>
                    </Col>
                  );
                })}
              </Row>
            </Panel>
          )}
        </Collapse>
      </div>
    );
  }
}
